/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { v4 as uuid_v4 } from "uuid";

import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import { Dialog } from "../../../dialogs/Dialog";
import { IEditor } from "../../../IEditor";
import { ResourceService } from "../../../resources/ResourceService";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { ThemeCreateComponent } from "./ThemeCreateComponent";
import { ToastService } from "../../../toasts/ToastService";
import { TOAST_TYPE } from "../../../toasts/IToast";
import { normalizeResourcePath } from "../../../resources/ResourceHelpers";
import { ThemeModel } from "../Common/ThemeModel";

export interface ThemeCreate_Params {
	basePath?: string;
}

export interface ThemeCreate_Result {
	resourceId: string;
}

export interface IThemeCreate_CreateParams {
	basePath: string;
	name: string;
}

export class ThemeCreate extends Dialog<ThemeCreate_Params, ThemeCreate_Result> {
	/** Services */
	private readonly resourceSvc: ResourceService;
	private readonly runtimeSvc: RuntimeService;
	private readonly toastSvc: ToastService;

	/** New resource ID */
	private readonly resourceId = uuid_v4();

	/** Document model */
	private themeModel: ThemeModel;

	/** If action is being saved */
	private saveInProgress = false;

	/** Event emitted when save progress changes */
	public readonly onSaveStateChange = createEventEmitter<void>();

	/**
	 * Dialog constructor
	 *
	 * @param dc Dependency container
	 * @param id Dialog ID
	 * @param params Open params
	 */
	public constructor(dc: IEditor, id: string, params: ThemeCreate_Params) {
		super(dc, id, params);

		this.resourceSvc = dc.get("resourceService");
		this.runtimeSvc = dc.get("runtimeService");
		this.toastSvc = dc.get("toastService");

		this.resourceId = uuid_v4();

		this.themeModel = new ThemeModel(this.runtimeSvc);
		this.themeModel.createDefault();

		const idModel = this.themeModel.getModel().props.id;
		idModel.schema.setValue(idModel, this.resourceId, false);
	}

	public getComponent(): typeof ThemeCreateComponent {
		return ThemeCreateComponent;
	}

	public getThemeModel(): ThemeModel {
		return this.themeModel;
	}

	/**
	 * Creates a resource
	 * Returns false if create is already in progress or resource was successfully created
	 */
	public async create(params: IThemeCreate_CreateParams): Promise<boolean> {
		if (this.saveInProgress) {
			return false;
		}

		const content = this.themeModel.exportDocumentAsYAML();

		this.saveInProgress = true;
		emitEvent(this.onSaveStateChange);

		try {
			await this.resourceSvc.createBlueprint(params.basePath, params.name, content, this.resourceId);

			this.toastSvc.add({
				type: TOAST_TYPE.SUCCESS,
				// @todo Translate
				message: "Theme has been created.",
				timeoutMs: 2500
			});

			this.close({
				resourceId: this.resourceId
			});
		} catch (err) {
			this.toastSvc.add({
				type: TOAST_TYPE.ERROR,
				// @todo Translate
				message: "Failed to create theme. Please try it again in a while.",
				details: String(err)
			});
		} finally {
			this.saveInProgress = false;
			emitEvent(this.onSaveStateChange);
		}

		return true;
	}

	/**
	 * Returns if an action is being saved
	 */
	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	/**
	 * Returns if a resource with given baseName and name already exists
	 *
	 * @param basePath Base path
	 * @param name Name
	 */
	public checkIfBlueprintExists(basePath: string, name: string): boolean {
		return !!this.resourceSvc.getResourceByPath(
			normalizeResourcePath(basePath, name, { sanitize: true })
		);
	}
}
